/**
 * Safe date/period formatting helpers.
 * Avoids classic bugs: `new Date('YYYY-MM-DD')` (UTC midnight) and
 * `date.toISOString().slice(0, 10)` (UTC day shift in positive offsets).
 */

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function parseDateOnly(value) {
  if (!value) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return {
      year: value.getFullYear(),
      month: value.getMonth() + 1,
      day: value.getDate(),
    };
  }
  const raw = String(value).trim();
  const match = raw.match(DATE_ONLY_RE);
  if (match) {
    return {
      year: Number(match[1]),
      month: Number(match[2]),
      day: Number(match[3]),
    };
  }
  // Datetime / ISO — take calendar day in local timezone (or explicit TZ below).
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return null;
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  };
}

export function formatDateOnly(value, { fallback = '—' } = {}) {
  const parts = parseDateOnly(value);
  if (!parts) return fallback;
  const dd = String(parts.day).padStart(2, '0');
  const mm = String(parts.month).padStart(2, '0');
  return `${dd}.${mm}.${parts.year}`;
}

export function formatDateTimeInZone(value, timeZone, { fallback = '—', withTime = true } = {}) {
  if (!value) return fallback;
  try {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return fallback;
    return new Intl.DateTimeFormat('ru-RU', {
      dateStyle: 'medium',
      ...(withTime ? { timeStyle: 'short' } : {}),
      ...(timeZone ? { timeZone } : {}),
    }).format(date);
  } catch {
    return fallback;
  }
}

/** Calendar day of a datetime in the given IANA timezone (YYYY-MM-DD parts). */
export function calendarDayInZone(value, timeZone) {
  if (!value) return null;
  try {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timeZone || undefined,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const formatted = formatter.format(date); // YYYY-MM-DD in en-CA
    return parseDateOnly(formatted);
  } catch {
    return parseDateOnly(value);
  }
}

export function formatPeriodRange(start, end, { timeZone, dateOnly = false } = {}) {
  if (!start || !end) return '—';
  if (dateOnly || (DATE_ONLY_RE.test(String(start)) && DATE_ONLY_RE.test(String(end)))) {
    return `${formatDateOnly(start)} — ${formatDateOnly(end)}`;
  }
  if (timeZone) {
    const startDay = calendarDayInZone(start, timeZone);
    const endDay = calendarDayInZone(end, timeZone);
    if (startDay && endDay) {
      return `${formatDateOnly(startDay)} — ${formatDateOnly(endDay)}`;
    }
  }
  return `${formatDateOnly(start)} — ${formatDateOnly(end)}`;
}

/** Local calendar YYYY-MM for "now" minus month offset (no UTC shift). */
export function localYearMonth(offsetMonths = 0) {
  const now = new Date();
  const date = new Date(now.getFullYear(), now.getMonth() - offsetMonths, 1);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

/** First day of local month as YYYY-MM-DD (for APIs that expect a date in the month). */
export function localMonthStartDate(offsetMonths = 0) {
  return `${localYearMonth(offsetMonths)}-01`;
}

/** Last calendar day of YYYY-MM as YYYY-MM-DD. */
export function localMonthEndDate(yearMonth) {
  const [year, month] = String(yearMonth || '').split('-').map(Number);
  if (!year || !month) return '';
  const lastDay = new Date(year, month, 0).getDate();
  return `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
}

/**
 * Build reporting window for a report month from building period-day settings.
 * If days are not both set → calendar month (same rule as backend resolve_month_window).
 */
export function buildReportPeriodWindow(reportMonth, startDay, endDay) {
  const [yearValue, monthValue] = String(reportMonth || '').split('-').map(Number);
  if (!yearValue || !monthValue) {
    return { period_start: '', period_end: '' };
  }

  const startNum = Number(startDay);
  const endNum = Number(endDay);
  const hasCustom =
    Number.isFinite(startNum) &&
    Number.isFinite(endNum) &&
    startNum >= 1 &&
    startNum <= 31 &&
    endNum >= 1 &&
    endNum <= 31;

  if (!hasCustom) {
    return {
      period_start: `${yearValue}-${String(monthValue).padStart(2, '0')}-01`,
      period_end: localMonthEndDate(reportMonth),
    };
  }

  const endMonthIndex = monthValue - 1;
  const startMonthOffset = startNum > endNum ? -1 : 0;
  const startDate = new Date(yearValue, endMonthIndex + startMonthOffset, 1);
  const startYear = startDate.getFullYear();
  const startMonth = startDate.getMonth() + 1;
  const startMax = new Date(startYear, startMonth, 0).getDate();
  const endMax = new Date(yearValue, monthValue, 0).getDate();
  const safeStart = Math.min(startNum, startMax);
  const safeEnd = Math.min(endNum, endMax);

  return {
    period_start: `${startYear}-${String(startMonth).padStart(2, '0')}-${String(safeStart).padStart(2, '0')}`,
    period_end: `${yearValue}-${String(monthValue).padStart(2, '0')}-${String(safeEnd).padStart(2, '0')}`,
  };
}

export function formatPeriodRuleLabel(startDay, endDay) {
  const startNum = Number(startDay);
  const endNum = Number(endDay);
  if (
    Number.isFinite(startNum) &&
    Number.isFinite(endNum) &&
    startNum >= 1 &&
    endNum >= 1
  ) {
    return `Правило периода: ${startNum}–${endNum}`;
  }
  return 'Правило периода: календарный месяц';
}
